//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as instagramAudienceMetricsApi from '@apis/gateway-instagram/instagramAudienceMetricsApi';
import { IInstagramOnlineFollowersHeatmap } from '@apis/gateway-instagram/instagramAudienceMetricsModel';
import {
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { IInstagramAudienceDailyMetrics } from '@instagram-account/models/IInstagramAudienceDailyMetrics';
import { IInstagramAudienceLifetimeMetrics } from '@instagram-account/models/IInstagramAudienceLifetimeMetrics';
import { IInstagramAudienceState } from '@instagram-account/redux/audiencev2/instagramAudienceModel';
import { parseInstagramDailyAudienceMetricsResponse } from '@instagram-account/redux/audiencev2/instagramAudienceUtils';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import TimeZoneId from '@models/TimeZoneId';
import { RequestError } from '@utils/sessionFetch';

const initialState: IInstagramAudienceState = {
  errors: {},
  flags: {
    isFetchingDailyAudienceMetrics: false,
    isFetchingLifetimeAudienceMetrics: false,
    isFetchingOnlineFollowersHeatmap: false,
  },
  selectedDateRange: getAliasedDateRange(
    'last7Days',
    TimeZoneId.AmericaLosAngeles,
  ),
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramAccountAudience,
  initialState,
  reducers: {
    requestDailyAudienceMetrics(state) {
      state.flags.isFetchingDailyAudienceMetrics = true;
      state.errors.fetchDailyAudienceMetricsError = undefined;
    },
    receiveDailyAudienceMetricsSucceeded(
      state,
      action: PayloadAction<IInstagramAudienceDailyMetrics>,
    ) {
      state.flags.isFetchingDailyAudienceMetrics = false;
      state.dailyAudienceMetrics = action.payload;
    },
    receiveDailyAudienceMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingDailyAudienceMetrics = false;
      state.errors.fetchDailyAudienceMetricsError = action.payload;
    },
    requestLifetimeAudienceMetrics(state) {
      state.flags.isFetchingLifetimeAudienceMetrics = true;
      state.errors.fetchLifetimeAudienceMetricsError = undefined;
    },
    receiveLifetimeAudienceMetrics(
      state,
      action: PayloadAction<IInstagramAudienceLifetimeMetrics>,
    ) {
      state.flags.isFetchingLifetimeAudienceMetrics = false;
      state.lifetimeAudienceMetrics = action.payload;
    },
    receiveLifetimeAudienceMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingLifetimeAudienceMetrics = false;
      state.errors.fetchLifetimeAudienceMetricsError = action.payload;
    },
    requestOnlineFollowersHeatmap(state) {
      state.flags.isFetchingOnlineFollowersHeatmap = true;
      state.errors.fetchOnlineFollowersHeatmapError = undefined;
    },
    receiveOnlineFollowersHeatmap(
      state,
      action: PayloadAction<IInstagramOnlineFollowersHeatmap>,
    ) {
      state.flags.isFetchingOnlineFollowersHeatmap = false;
      state.onlineFollowersHeatmap = action.payload;
    },
    receiveOnlineFollowersHeatmapFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingOnlineFollowersHeatmap = false;
      state.errors.fetchOnlineFollowersHeatmapError = action.payload;
    },
    updateSelectedDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.selectedDateRange = action.payload;
    },
  },
});

export const { updateSelectedDateRange } = actions;

/**
 * Fetches a complete set of audience metrics, including aggregate stats for a given date range.
 */
export const fetchDailyAudienceMetrics = createThunk(
  () => async (dispatch, getState) => {
    const { flags, selectedDateRange } = getState().instagram.audienceV2;
    if (flags.isFetchingDailyAudienceMetrics) {
      return;
    }

    dispatch(actions.requestDailyAudienceMetrics());
    try {
      const dailyAudienceMetricsResponse = await instagramAudienceMetricsApi.getDailyAudienceMetrics(
        {
          startTime: selectedDateRange.startDate,
          endTime: selectedDateRange.endDate,
        },
      );

      const dailyAudienceMetrics = parseInstagramDailyAudienceMetricsResponse(
        dailyAudienceMetricsResponse,
      );

      dispatch(
        actions.receiveDailyAudienceMetricsSucceeded(dailyAudienceMetrics),
      );
    } catch (e) {
      const err = e as RequestError;
      dispatch(actions.receiveDailyAudienceMetricsFailed(err.message));
    }
  },
);

/**
 * Fetches the latest instagram lifetime audience metrics
 */
export const fetchLifetimeAudienceMetrics = createThunk(
  () => async (dispatch, getState) => {
    const { flags } = getState().instagram.audienceV2;
    if (flags.isFetchingLifetimeAudienceMetrics) {
      return;
    }

    dispatch(actions.requestLifetimeAudienceMetrics());
    try {
      const lifetimeAudienceMetricsResponse = await instagramAudienceMetricsApi.getLatestLifetimeMetrics();

      dispatch(
        actions.receiveLifetimeAudienceMetrics(
          lifetimeAudienceMetricsResponse.lifetimeMetrics,
        ),
      );
    } catch (e) {
      const err = e as RequestError;
      dispatch(actions.receiveLifetimeAudienceMetricsFailed(err.message));
    }
  },
);

/**
 * Fetches the latest Instagram audience online followers heatmap
 */
export const fetchOnlineFollowersHeatMap = createThunk(
  () => async (dispatch, getState) => {
    const { flags } = getState().instagram.audienceV2;
    if (flags.isFetchingOnlineFollowersHeatmap) {
      return;
    }

    dispatch(actions.requestOnlineFollowersHeatmap());
    try {
      const onlineFollowersHeatmap = await instagramAudienceMetricsApi.getOnlineFollowersHeatmap();
      dispatch(actions.receiveOnlineFollowersHeatmap(onlineFollowersHeatmap));
    } catch (e) {
      const err = e as RequestError;
      dispatch(actions.receiveOnlineFollowersHeatmapFailed(err.message));
    }
  },
);

export default reducer;
